/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  viewerMutationOptions,
  viewerQueryKeys,
  viewerQueryOptions,
  type AreaTag as ViewerAreaTag,
  type LayoutMarker as ViewerLayoutMarker,
  type ViewerLayout,
  type ViewerPhoto,
} from '@ironview/interactive-viewer';
import {
  createIronview360ViewerContext,
  ironview360ViewerAdapter,
} from '../adapters/ironview360DataAdapter';
import {
  fromViewerAreaTag,
  fromViewerAreaTagRecord,
  fromViewerLayout,
  fromViewerLayoutMarker,
  fromViewerPhoto,
  toViewerLayoutMarker,
} from '../adapters/ironview360Adapter';
import { getLayoutPdfInfo, LayoutPdfInfo } from '../api/layout-pdf-api';
import {
  Layout,
  PhotoMarker,
  Photo,
  SaveMarkersData,
  SaveMarkersResponse,
  Unit,
} from '../types/types';
import type { AreaTag as Ironview360AreaTag } from '@/features/area-tags/types/area-tags';
import { ApiError } from '@/shared/errors/app-error';

export const useLayouts = (projectId: string | undefined, type?: string) => {
  const context = createIronview360ViewerContext(projectId ?? '', type);
  return useQuery<readonly ViewerLayout[], Error, Layout[]>({
    ...viewerQueryOptions.layouts(ironview360ViewerAdapter, context),
    enabled: !!projectId,
    select: (layouts) => layouts.map(fromViewerLayout),
    staleTime: 1000 * 60 * 5,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
        return false;
      }
      return failureCount < 2;
    },
    // Layout payloads contain signed asset URLs. Background refreshes replaced
    // those URLs and caused an active PDF editor to remount mid-session.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const usePhotoMarkers = (layoutId: string | undefined, projectId?: string) => {
  const context = createIronview360ViewerContext(projectId ?? '');
  return useQuery<readonly ViewerLayoutMarker[], Error, PhotoMarker[]>({
    ...viewerQueryOptions.markers(ironview360ViewerAdapter, context, layoutId ?? ''),
    enabled: !!layoutId && !!projectId,
    select: (markers) => markers.map(fromViewerLayoutMarker),
    staleTime: 1000 * 60 * 60 * 24,
  });
};

export const useAvailablePhotos = (layoutId: string | undefined, projectId?: string) => {
  const context = createIronview360ViewerContext(projectId ?? '');
  return useQuery<readonly ViewerPhoto[], Error, Photo[]>({
    ...viewerQueryOptions.availablePhotos(ironview360ViewerAdapter, context, layoutId ?? ''),
    enabled: !!layoutId && !!projectId,
    select: (photos) => photos.map(fromViewerPhoto),
    staleTime: 1000 * 60 * 60 * 24,
  });
};

export const useAreaTags = (layoutId: string | undefined, projectId?: string) => {
  const context = createIronview360ViewerContext(projectId ?? '');
  return useQuery<readonly ViewerAreaTag[], Error, Unit[]>({
    ...viewerQueryOptions.areaTags(ironview360ViewerAdapter, context, layoutId ?? ''),
    enabled: !!layoutId && !!projectId,
    select: (areaTags) => areaTags.map(fromViewerAreaTag),
    staleTime: 1000 * 60 * 5,
  });
};

export const useAreaTagRecords = (layoutId: string | undefined, projectId?: string) => {
  const context = createIronview360ViewerContext(projectId ?? '');
  return useQuery<readonly ViewerAreaTag[], Error, Ironview360AreaTag[]>({
    ...viewerQueryOptions.areaTags(ironview360ViewerAdapter, context, layoutId ?? ''),
    enabled: !!layoutId && !!projectId,
    select: (areaTags) => areaTags.map(fromViewerAreaTagRecord),
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Fetch the minimal PDF metadata needed to render a PDF-based layout
 * (page #, dimensions, rotation, dpi, signed URL).
 *
 * Uses the layout-scoped pdf-info endpoint instead of the drawings
 * PDFPage endpoint so non-staff users in the layout's project can
 * always read it, even when the underlying PDFFile lives in a
 * different project (e.g. layouts created via copy-from-existing).
 */
export const useLayoutPdfInfo = (layoutId: string | number | null | undefined) => {
  return useQuery<LayoutPdfInfo>({
    queryKey: ['layoutPdfInfo', layoutId != null ? String(layoutId) : null],
    queryFn: () => getLayoutPdfInfo(layoutId!),
    enabled: layoutId != null && layoutId !== '',
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useSavePhotoMarkers = (projectId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    SaveMarkersResponse,
    Error,
    SaveMarkersData,
    {
      queryKey?: ReturnType<typeof viewerQueryKeys.markers>;
      previousMarkers?: readonly ViewerLayoutMarker[];
    }
  >({
    mutationFn: async (data) => {
      if (!projectId) {
        throw new Error('Project ID is required for saving markers');
      }
      const context = createIronview360ViewerContext(projectId);
      await viewerMutationOptions.saveMarkers(ironview360ViewerAdapter).mutationFn({
        context,
        layoutId: data.layoutId,
        markers: data.markers.map(toViewerLayoutMarker),
      });
      return {
        status: 'success',
        message: 'Markers updated successfully',
      };
    },
    onMutate: async (data) => {
      if (!projectId) return {};
      const queryKey = viewerQueryKeys.markers(
        ironview360ViewerAdapter.id,
        projectId,
        data.layoutId
      );
      await queryClient.cancelQueries({ queryKey });
      const previousMarkers = queryClient.getQueryData<readonly ViewerLayoutMarker[]>(queryKey);
      queryClient.setQueryData<readonly ViewerLayoutMarker[]>(
        queryKey,
        data.markers.map(toViewerLayoutMarker)
      );
      return { queryKey, previousMarkers };
    },
    onError: (_error, _variables, mutationContext) => {
      if (mutationContext?.queryKey) {
        queryClient.setQueryData(mutationContext.queryKey, mutationContext.previousMarkers);
      }
    },
    onSettled: (_data, _error, variables, mutationContext) => {
      const queryKey =
        mutationContext?.queryKey ??
        (projectId
          ? viewerQueryKeys.markers(ironview360ViewerAdapter.id, projectId, variables.layoutId)
          : undefined);
      if (queryKey) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
};

export const useLayoutData = (layoutId: string | undefined, projectId: string | undefined) => {
  const areaTags = useAreaTags(layoutId, projectId);
  const photoMarkers = usePhotoMarkers(layoutId, projectId);
  const availablePhotos = useAvailablePhotos(layoutId, projectId);

  return React.useMemo(
    () => ({
      areaTags: areaTags.data || [],
      photoMarkers: photoMarkers.data || [],
      availablePhotos: availablePhotos.data || [],
      isLoading: areaTags.isLoading || photoMarkers.isLoading || availablePhotos.isLoading,
      isFetching: areaTags.isFetching || photoMarkers.isFetching || availablePhotos.isFetching,
      error: areaTags.error || photoMarkers.error || availablePhotos.error,
      refetch: () => {
        areaTags.refetch();
        photoMarkers.refetch();
        availablePhotos.refetch();
      },
    }),
    [
      areaTags.data,
      photoMarkers.data,
      availablePhotos.data,
      areaTags.isLoading,
      photoMarkers.isLoading,
      availablePhotos.isLoading,
      areaTags.isFetching,
      photoMarkers.isFetching,
      availablePhotos.isFetching,
      areaTags.error,
      photoMarkers.error,
      availablePhotos.error,
    ]
  );
};
